import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../hooks/useTranslation";
import { fetchPatientProfilesThunk, createPatientProfileThunk, deletePatientProfileThunk } from "../../store/slices/patientProfilesReducer";
import PatientCard from "../../components/kostData/PatientCard";
import AddPatientCard from "../../components/kostData/AddPatientCard";
import JourneyBreadcrumb from "../../components/navigation/JourneyBreadcrumb";
import { useAppAlertDialog } from "../../hooks/useAppAlertDialog";

const PatientListPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { profiles, loading, error } = useSelector((state) => state.patientProfiles);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const { showAlert, dialogNode } = useAppAlertDialog({
    closeLabel: t("Stäng"),
  });

  useEffect(() => {
    dispatch(fetchPatientProfilesThunk({ status: statusFilter === 'all' ? null : statusFilter, search: searchTerm || null }));
    
    const timer = setTimeout(() => {
      setIsPageLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, [dispatch, statusFilter, searchTerm]);

  const handleAddPatient = async (patientData) => {
    try {
      await dispatch(createPatientProfileThunk(patientData)).unwrap();
    } catch (error) {
      throw error;
    }
  };

  const handleDeletePatient = async (patient) => {
    try {
      await dispatch(deletePatientProfileThunk(patient.id)).unwrap();
    } catch (error) {
      console.error("Failed to delete patient:", error);
      showAlert(t("Fel"), t("Kunde inte ta bort patienten. Försök igen."));
    }
  };

  const filteredProfiles = profiles.filter(profile => {
    if (searchTerm && !profile.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (statusFilter !== 'all' && profile.status !== statusFilter) {
      return false;
    }
    return true;
  });

  if (loading && profiles.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-gray-200 rounded-full animate-pulse"></div>
            <div className="absolute top-0 left-0 w-20 h-20 border-4 border-secondary-green border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="mt-8 text-gray-600 text-lg font-medium">
            Laddar patienter...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 transition-all duration-500 ease-out ${
        isPageLoaded ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 tablet:px-6 laptop:px-8 desktop:px-8 py-8">
        <JourneyBreadcrumb
          items={[
            { label: t("Dashboard") || "Dashboard", to: "/" },
            { label: t("Patientprofiler") || "Patientprofiler" },
          ]}
          className="mb-6"
        />

        {/* Header */}
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <h1 className="text-xl mobile:text-2xl tablet:text-3xl font-bold text-gray-900 mb-4">
            {t("Patientprofiler") || "Patientprofiler"}
          </h1>

          {/* Search and Filter */}
          <div className="flex flex-col mobile:flex-col tablet:flex-row laptop:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder={t("Sök patienter...") || "Sök patienter..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-green focus:border-transparent"
              />
            </div>
            <div className="w-full mobile:w-full tablet:w-48 laptop:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-green focus:border-transparent"
              >
                <option value="all">{t("Alla") || "Alla"}</option>
                <option value="active">{t("Aktiva") || "Aktiva"}</option>
                <option value="inactive">{t("Inaktiva") || "Inaktiva"}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Patients Grid */}
        <div className="grid grid-cols-1 mobile:grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-4 mobile:gap-4 tablet:gap-4 laptop:gap-6 desktop:gap-6">
          <AddPatientCard onAddPatient={handleAddPatient} />
          {filteredProfiles.map((patient) => (
            <PatientCard
              key={patient.id}
              patient={patient}
              onDelete={handleDeletePatient}
            />
          ))}
        </div>

        {/* Empty State */}
        {!loading && filteredProfiles.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">
              {searchTerm || statusFilter !== 'all'
                ? t("Inga patienter hittades") || "Inga patienter hittades"
                : t("Inga patienter ännu. Lägg till din första patient!") || "Inga patienter ännu. Lägg till din första patient!"}
            </p>
          </div>
        )}
      </div>
      {dialogNode}
    </div>
  );
};

export default PatientListPage;

